import { useEffect, useRef, useState } from 'react'
import { useChannel } from 'ably/react'
import { useAbly } from '@/contexts/AblyGlobalProvider'
import { compressToBase64, decompressFromBase64 } from 'lz-string'
import { useAxios } from '@/contexts/AxiosProvider'
import { PubSubMessage } from '@db8/shared'
import { useUser } from '@clerk/clerk-react'

const BATCH_DELAY = 250
const MESSAGE_RATE_LIMIT = 5

let batchingTimeout: ReturnType<typeof setTimeout> | null = null
let messageQueue: Record<string, any[]> = {}
let lastSentPayload = ''
let totalMessagesPublished = 0
let messagesThisSecond = 0
let shutdown = false

setInterval(() => {
  messagesThisSecond = 0
}, 1000)

export const isShutdown = () => shutdown
export const resetShutdown = () => {
  shutdown = false
}

type SyncOptions<T> = {
  url: string
  instant?: boolean
}

export const useAblyTypedPubSub = <T>(
  messageType: string,
  onData: (message: PubSubMessage) => void,
  sync?: SyncOptions<any>,
) => {
  const { meetingId } = useAbly()
  const [hasSyncedOnce, setHasSyncedOnce] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const axios = useAxios()
  const { user: clerkUser } = useUser()

  const getCurrentDbState = async () => {
    try {
      setIsFetching(true)
      const res = await axios.get(sync.url)

      if (res.data !== null && typeof res.data === 'object') {
        // no-op
        onData({ data: res.data, sender: null })
        setHasSyncedOnce(true)
      } else {
        // no-op
      }
    } catch (err) {
      // no-op
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    if (sync) getCurrentDbState()
  }, [sync?.url])

  const { channel } = useChannel(meetingId, (message) => {
    if (!sync || hasSyncedOnce) {
      if (message.name === '__batch__') {
        try {
          const decompressed = decompressFromBase64(message.data)
          const parsed: Record<string, string[]> = JSON.parse(decompressed)
          const messages = parsed[messageType] || []

          for (const raw of messages) {
            const parsedMessage: PubSubMessage = JSON.parse(raw)
            onData(parsedMessage)
          }
        } catch (err) {
          // no-op
        }
      } else if (message.name === messageType) {
        try {
          const parsed: PubSubMessage = JSON.parse(message.data)
          onData(parsed)
        } catch (err) {
          // no-op
        }
      }
    }
  })

  const publish = (data: T) => {
    if (shutdown) {
      return
    }

    const payload: PubSubMessage = {
      data,
      sender: clerkUser?.id,
    }

    const serialized = JSON.stringify(payload)

    // * Remove delay where instant sync is required (Ex: audio syncing)
    if (sync?.instant) {
      channel?.publish(messageType, serialized)
      totalMessagesPublished++
      messagesThisSecond++
      return
    }

    if (!messageQueue[messageType]) messageQueue[messageType] = []
    if (!messageQueue[messageType].includes(serialized)) {
      messageQueue[messageType].push(serialized)
    }

    // * Batch similar request to lower request load/ avoid timing issues
    if (!batchingTimeout) {
      batchingTimeout = setTimeout(() => {
        const payload = JSON.stringify(messageQueue)
        const compressed = compressToBase64(payload)

        if (compressed !== lastSentPayload) {
          channel?.publish('__batch__', compressed)
          lastSentPayload = compressed

          totalMessagesPublished++
          messagesThisSecond++

          if (messagesThisSecond > MESSAGE_RATE_LIMIT) {
            shutdown = true
          }
        }

        messageQueue = {}
        batchingTimeout = null
      }, BATCH_DELAY)
    }
  }

  return { publish, isFetching, hasSyncedOnce }
}
